import { create } from 'zustand'
import { persist, createJSONStorage } from 'zustand/middleware'
import type { Branch } from '@/types/api'

/**
 * Auth Store - manages authentication state.
 *
 * IMPORTANT: This store uses the SAME localStorage keys as v1 to share auth state.
 * The v1 app stores tokens with branch-prefixed keys like 'stable-token' or 'dev-token'.
 * This allows seamless auth sharing between v1 and v2.
 */

interface AuthState {
  // Current token (cached from localStorage)
  token: string | null
  // Current username (cached from localStorage)
  username: string | null
  // Current branch (stable or dev)
  branch: Branch
  // Computed auth status
  isAuthenticated: boolean
}

interface AuthActions {
  // Initialize auth state from localStorage (v1 compatible keys)
  initialize: () => void
  // Set the current branch
  setBranch: (branch: Branch) => void
  // Get token for current branch (reads from v1-compatible localStorage)
  getToken: () => string | null
  // Get username for current branch
  getUsername: () => string | null
  // Set token (updates v1-compatible localStorage)
  setToken: (token: string) => void
  // Set username (updates v1-compatible localStorage)
  setUsername: (username: string) => void
  // Clear auth (logout)
  logout: () => void
}

type AuthStore = AuthState & AuthActions

/**
 * Detect the current branch from the hostname.
 * Alpha-Trader uses subdomains: stable.alpha-trader.com, dev.alpha-trader.com, devui.alpha-trader.com
 */
function detectBranch(): Branch {
  const hostname = window.location.hostname

  // Check for dev subdomains (dev. or devui.)
  if (hostname.startsWith('dev.') || hostname.startsWith('devui.')) {
    return 'dev'
  }

  // Check for localhost with dev port (optional convention)
  if (hostname === 'localhost' && window.location.port === '5174') {
    return 'dev'
  }

  // Default to stable
  return 'stable'
}

/**
 * Get a v1-compatible localStorage key.
 * v1 uses keys like 'stable-token', 'dev-username', etc.
 */
function getStorageKey(branch: Branch, key: string): string {
  return `${branch}-${key}`
}

export const useAuthStore = create<AuthStore>()(
  persist(
    (set, get) => ({
      // Initial state
      token: null,
      username: null,
      branch: detectBranch(),
      isAuthenticated: false,

      // Initialize from v1-compatible localStorage
      initialize: () => {
        const branch = get().branch
        const token = localStorage.getItem(getStorageKey(branch, 'token'))
        const username = localStorage.getItem(getStorageKey(branch, 'username'))

        set({
          token,
          username,
          isAuthenticated: !!token,
        })
      },

      // Set branch and re-initialize auth
      setBranch: (branch: Branch) => {
        set({ branch })
        get().initialize()
      },

      // Get token (always reads fresh from localStorage for v1 compatibility)
      getToken: () => {
        const branch = get().branch
        return localStorage.getItem(getStorageKey(branch, 'token'))
      },

      // Get username (always reads fresh from localStorage for v1 compatibility)
      getUsername: () => {
        const branch = get().branch
        return localStorage.getItem(getStorageKey(branch, 'username'))
      },

      // Set token (updates both store and v1-compatible localStorage)
      setToken: (token: string) => {
        const branch = get().branch
        localStorage.setItem(getStorageKey(branch, 'token'), token)
        set({
          token,
          isAuthenticated: true,
        })
      },

      // Set username (updates both store and v1-compatible localStorage)
      setUsername: (username: string) => {
        const branch = get().branch
        localStorage.setItem(getStorageKey(branch, 'username'), username)
        set({ username })
      },

      // Logout - clear auth data
      logout: () => {
        const branch = get().branch
        localStorage.removeItem(getStorageKey(branch, 'token'))
        localStorage.removeItem(getStorageKey(branch, 'username'))
        set({
          token: null,
          username: null,
          isAuthenticated: false,
        })
      },
    }),
    {
      name: 'alpha-trader-v2-auth',
      storage: createJSONStorage(() => sessionStorage),
      // Only persist branch preference, not auth data (that's in v1-compatible localStorage)
      partialize: (state) => ({ branch: state.branch }),
    }
  )
)

// Initialize auth state on store creation
if (typeof window !== 'undefined') {
  useAuthStore.getState().initialize()
}
